"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { endOfMonth, format, isEqual, isPast, startOfMonth } from "date-fns";
import { AlertTriangle, Calendar, ChevronRight, Clock } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookingForm } from "../components/booking-form";
import { Page } from "../../types/page";
import { getLocationIcon } from "../components/location-icon";
import { Availability } from "../../types/availability";
import { ApiEndpoint, apiService } from "../../lib/api";
import { setEventDates } from "../../helper/eventDates";
import { SelectedSlot, Slots, TimeSlot } from "../../types/slots";
import { Booking } from "../../types/booking";
import { BookingSuccess } from "../components/booking-success";
import { markdownToHtml } from "../../lib/markdown-to-html";
import { toast } from "sonner";
import { SlotTime } from "../components/slot-time";
import { Skeleton } from "../../components/ui/skeleton";
import { Host } from "../../types/hosts";
import { browserTimezone, getInitial } from "../../helper/utility";
import { DialogTitle } from "@radix-ui/react-dialog";

interface WebinarBookingProps {
  page: Page;
  resource: any;
  onDone: () => void;
}

export function WebinarBooking({ page, resource }: WebinarBookingProps) {
  const [currentStep, setCurrentStep] = useState<"slots" | "success">("slots");
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showSlotSelector, setShowSlotSelector] = useState(false);
  const [location, setLocation] = useState(resource.locations?.[0]);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [cohosts, setCohosts] = useState<Host[]>([]);
  const [speakers, setSpeakers] = useState<Host[]>([]);

  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot>();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [booking, setBooking] = useState<Booking>({} as Booking);

  const isPhysicalLocation = location?.type === "physical";

  useEffect(() => {
    setLocation(resource.locations?.[0]);
    setHosts(
      resource.settings.hosts?.filter((x: any) => x.type === "host") || []
    );
    setCohosts(
      resource.settings.hosts?.filter((x: any) => x.type === "cohost") || []
    );
    setSpeakers(
      resource.settings.hosts?.filter((x: any) => x.type === "speaker") || []
    );
  }, [resource]);

  const getAvailability = useCallback(
    async (date: Date) => {
      setIsLoading(true);
      setError(null);
      try {
        const startDate = format(startOfMonth(date), "yyyy-MM-dd");
        const endDate = format(endOfMonth(date), "yyyy-MM-dd");
        const response = await apiService.list(
          `${ApiEndpoint.availability}/${resource.resource_id}`,
          {
            start: startDate,
            end: endDate,
            time_zone: browserTimezone,
          }
        );
        setAvailability(response);
      } catch (err: any) {
        setError(err.message || "Failed to load time slots");
        setAvailability(null);
      } finally {
        setIsLoading(false);
      }
    },
    [resource.resource_id]
  );

  useEffect(() => {
    getAvailability(currentMonth);
  }, [currentMonth, getAvailability]);

  useEffect(() => {
    if (availability && !selectedSlot) {
      const data = setEventDates(availability);
      if (data?.start_at) {
        setSelectedSlot(data);
      }
    }
  }, [availability, selectedSlot]);

  // Check if multiple slots are available
  const hasMultipleSlots = availability?.slots && availability.slots.length > 1;
  const totalAvailableSlots =
    availability?.slots?.reduce((total: number, dateSlot: Slots) => {
      return total + dateSlot.hours.length;
    }, 0) || 0;

  const handleSlotSelection = (dateSlot: Slots, timeSlot: TimeSlot) => {
    const newSelectedSlot: SelectedSlot = {
      start_at: new Date(timeSlot.time),
      end_at: new Date(
        new Date(timeSlot.time).getTime() +
          (availability?.duration || 60) * 60 * 1000
      ),
      available: timeSlot.available,
      duration: availability?.duration || 60,
      time_zone: availability?.time_zone!,
    };
    setSelectedSlot(newSelectedSlot);
    setShowSlotSelector(false);
  };

  const handleBookingSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const finalBooking: Booking = {
        ...selectedSlot,
        ...data,
        booking_url: `https://${page.domain}/${resource.slug}`,
        resource_id: resource.resource_id,
        resource: {
          resource_id: resource.resource_id,
        },
      };
      const response = await apiService.post(
        ApiEndpoint.bookings,
        finalBooking
      );
      setBooking(response);
      setCurrentStep("success");
    } catch (err: any) {
      toast.error(err.message || "Something went wrong, please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatSlotTime = (timeString: string) => {
    const date = new Date(timeString);
    return {
      date: format(date, "EEE, MMM d"),
      time: format(date, "h:mm a"),
    };
  };

  return (
    <>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-8 p-6">
        {/* Left Column */}
        <div className="space-y-6">
          <div className="relative rounded-lg overflow-hidden">
            {resource.banner && (
              <img
                src={resource.banner.url}
                alt={resource.name}
                className="object-cover"
              />
            )}
          </div>
          {hosts.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Hosted By</h3>
                <div className="space-y-4">
                  {hosts.map((host) => (
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={host.avatar} />
                        <AvatarFallback>{getInitial(host.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{host.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {host.role}
                        </div>
                        <div className="text-sm">{host.about}</div>
                      </div>
                    </div>
                  ))}
                  {/* <div className="flex gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Instagram className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Linkedin className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Globe className="h-4 w-4" />
                  </Button>
                </div> */}
                </div>
              </CardContent>
            </Card>
          )}

          {cohosts.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Co-hosts</h3>
                <div className="space-y-4">
                  {cohosts.map((host) => (
                    <div key={host.name} className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={host.avatar} />
                        <AvatarFallback>{getInitial(host.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-sm">{host.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {host.role}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* <Button variant="outline" className="w-full" size="sm">
            <MessageCircle className="w-4 h-4 mr-2" />
            Contact Host
          </Button>

          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary"># AI</Badge>
            <Badge variant="secondary"># Technology</Badge>
            <Badge variant="secondary"># Innovation</Badge>
          </div> */}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <div>
            {location.city && (
              <Badge variant="secondary" className="mb-4">
                Featured in {location.city}
              </Badge>
            )}
            <h1 className="text-4xl font-bold mb-4">{resource.name}</h1>

            <div className="flex flex-col md:flex-row md:items-start md:gap-4">
              {isLoading ? (
                <div className="flex items-center gap-2 min-w-[275px]">
                  <Skeleton className="w-14 h-14 border rounded" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32 border rounded" />
                    <Skeleton className="h-3 w-24 border rounded" />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col min-w-[275px]">
                  <div className="flex items-center gap-2">
                    <SlotTime slot={selectedSlot} />
                  </div>
                  {(hasMultipleSlots || totalAvailableSlots > 1) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSlotSelector(true)}
                      className="mt-1 p-0 py-1 h-auto"
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Select Another Time
                    </Button>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 mt-4 md:mt-0">
                <div className="flex w-14 h-14 rounded-lg border items-center justify-center">
                  {getLocationIcon(location.type)}
                </div>
                <div>
                  <span>{location.name}</span>
                  <p className="text-muted-foreground text-sm">
                    {location.city
                      ? `${location.city}, ${location.state}`
                      : location.remarks}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold">
                    {resource.form?.heading || "Registration"}
                  </h2>
                </div>

                <div className="space-y-2">
                  <p className="text-sm">
                    {isLoading
                      ? "Loading..."
                      : !selectedSlot || isPast(selectedSlot.start_at)
                      ? "This event is ended"
                      : "Welcome! To join the event, please register below."}
                  </p>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => setShowBookingDialog(true)}
                    disabled={!selectedSlot || isPast(selectedSlot.start_at)}
                  >
                    Book Now <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {selectedSlot &&
                  !isPast(selectedSlot.start_at) &&
                  Number(selectedSlot.available) > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4" />
                      {resource.capacity.max >
                      Number(selectedSlot.available) ? (
                        <span className="text-sm">
                          Filling fast - Only {selectedSlot.available} seats
                          left
                        </span>
                      ) : (
                        <span className="text-sm">
                          {selectedSlot.available} seats left
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <div
              className="prose prose-sm dark:prose-invert max-w-none space-y-4"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(resource.description),
              }}
            />

            {speakers.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Speakers</h2>
                <div className="space-y-6">
                  {speakers.map((speaker) => (
                    <div key={speaker.name} className="flex items-start gap-4">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={speaker.avatar} />
                        <AvatarFallback>
                          {getInitial(speaker.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{speaker.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {speaker.role}
                        </div>
                        <div className="text-sm mt-1">{speaker.about}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isPhysicalLocation && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Location</h2>
                <Card>
                  <img
                    src="/maps-placeholder.jpg"
                    alt="Event Location"
                    className="w-full object-cover rounded-t-lg"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-lg">{location.name}</h3>
                    <p className="text-muted-foreground">
                      {location.address}, {location.city}, {location.state}{" "}
                      {location.zip_code}
                    </p>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Slot Selector Dialog */}
      <Dialog open={showSlotSelector} onOpenChange={setShowSlotSelector}>
        <DialogContent className="max-w-md max-h-[90vh]">
          <DialogTitle>
            <div className="text-xl font-semibold">Select Date & Time</div>
          </DialogTitle>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {availability?.slots?.map((dateSlot: Slots) => (
              <div key={dateSlot.date} className="space-y-2">
                <h3 className="font-medium text-sm text-muted-foreground">
                  {format(new Date(dateSlot.date), "EEEE, MMMM d, yyyy")}
                </h3>
                <div className="grid gap-2">
                  {dateSlot.hours.map((timeSlot: TimeSlot, index: number) => {
                    const { time } = formatSlotTime(timeSlot.time);
                    const isSelected =
                      selectedSlot?.start_at &&
                      isEqual(new Date(timeSlot.time), selectedSlot.start_at);
                    const isPastSlot = isPast(new Date(timeSlot.time));

                    return (
                      <Button
                        key={index}
                        variant={isSelected ? "default" : "outline"}
                        className={`justify-between h-auto py-3 ${
                          isPastSlot ? "opacity-50" : ""
                        }`}
                        onClick={() => handleSlotSelection(dateSlot, timeSlot)}
                        disabled={isPastSlot || timeSlot.available < 1}
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {timeSlot.available} seats
                          </Badge>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] p-0">
          {currentStep === "success" ? (
            <BookingSuccess
              resource={resource}
              booking={booking}
              isDialog={true}
            />
          ) : (
            <BookingForm
              page={page}
              resource={resource}
              onSubmit={handleBookingSubmit}
              onBack={() => setShowBookingDialog(false)}
              slot={selectedSlot!}
              isSubmitting={isSubmitting}
              isDialog={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
